import Swiper from 'swiper';
import { Pagination, Navigation } from 'swiper/modules';
import gsap from 'gsap';
import { getCursorBorderColor } from './cursor';

let gallerySwiperInstance = null;

export function initGallerySwiper() {
  const swiperContainer = document.querySelector('.gallery-swiper');
  if (!swiperContainer) {
    // Se esiste una vecchia istanza, distruggila
    if (gallerySwiperInstance) {
      gallerySwiperInstance.destroy(true, true);
      gallerySwiperInstance = null;
    }
    return;
  }

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Funzione per forzare il re-rendering degli SVG in Safari
  const forceSvgRendering = () => {
    if (isSafari) {
      const svgs = swiperContainer.querySelectorAll('.swiper-button-next svg, .swiper-button-prev svg');
      svgs.forEach(svg => {
        // Forza il re-rendering dell'SVG
        svg.style.display = 'none';
        svg.offsetHeight; // Trigger reflow
        svg.style.display = 'block';
        
        // Aggiungi proprietà CSS per Safari
        svg.style.webkitTransform = 'translateZ(0)';
        svg.style.transform = 'translateZ(0)';
        svg.style.webkitBackfaceVisibility = 'hidden';
        svg.style.backfaceVisibility = 'hidden';
      });
    }
  };

  // Se esiste una vecchia istanza, distruggila
  if (gallerySwiperInstance) {
    gallerySwiperInstance.destroy(true, true);
    gallerySwiperInstance = null;
  }

  // Funzioni per gestire il cursore personalizzato
  const addCursorClass = (className) => {
    const body = document.body;
    if (body) body.classList.add(className);
  };

  const removeCursorClass = (className) => {
    const body = document.body;
    if (body) body.classList.remove(className);
  };

  const updateBallCursor = (duration, borderWidth, scale, borderColor) => {
    const ball = document.getElementById('ball');
    if (ball && gsap) {
      gsap.to(ball, {
        duration: duration,
        borderWidth: borderWidth,
        scale: scale,
        borderColor: borderColor
      });
    }
  };

  const slideImages = () => swiperContainer.querySelectorAll('.swiper-slide img');

  // Configurazione Swiper avanzata (adattata dal codice jQuery originale)
  const galleryOptions = {
    modules: [Pagination, Navigation],
    direction: 'horizontal',
    simulateTouch: true,
    slidesPerView: 'auto',
    touchStartPreventDefault: false,
    spaceBetween: 10,
    centeredSlides: false,
    loop: false,
    mousewheelControl: false,
    centeredSlides: true,
    speed: 700,
    navigation: {
      nextEl: '.gallery-swiper .swiper-button-next',
      prevEl: '.gallery-swiper .swiper-button-prev',
    },
    pagination: {
      el: '.gallery-swiper .swiper-pagination',
      type: 'progressbar',
      clickable: true,
    },
    
    // CALLBACK DI SWIPER per gestire il rilascio (backup)
    on: {
      init: function() {
        // Fix per Safari - forza il rendering degli SVG dopo l'inizializzazione
        if (isSafari) {
          setTimeout(forceSvgRendering, 100);
        }
      },
      transitionEnd: function() {
        const images = slideImages();
        if (images.length > 0 && gsap) {
          gsap.to(images, {
            duration: 0.7,
            scale: 1
          });
        }
        removeCursorClass('drag-cursor');
        
        // Fix per Safari - forza il rendering degli SVG dopo ogni transizione
        if (isSafari) {
          setTimeout(forceSvgRendering, 50);
        }
      }
    }
  };

  // Inizializza Swiper
  gallerySwiperInstance = new Swiper('.gallery-swiper', galleryOptions);

  // Fix immediato per Safari - forza il rendering degli SVG al caricamento
  if (isSafari) {
    // Esegui il fix immediatamente e dopo un delay
    forceSvgRendering();
    setTimeout(forceSvgRendering, 500);
    setTimeout(forceSvgRendering, 1000);
  }

  // Gestione drag con pointer events (non interferisce con Swiper)
  const swiperWrapper = swiperContainer.querySelector('.swiper-wrapper');
  
  if (swiperWrapper) {
    // Usa pointer events per compatibilità universale mouse/touch
    swiperWrapper.addEventListener('pointerdown', (e) => {
      // Non bloccare l'evento, lascia che Swiper lo gestisca
      const images = slideImages();
      if (images.length > 0 && gsap) {
        gsap.to(images, {
          duration: 0.5,
          scale: 0.95
        });
      }
      addCursorClass('drag-cursor');
    });

    swiperWrapper.addEventListener('pointerup', () => {
      const images = slideImages();
      if (images.length > 0 && gsap) {
        gsap.to(images, {
          duration: 0.7,
          scale: 1
        });
      }
      removeCursorClass('drag-cursor');
    });

    // Gestisci anche quando il pointer esce dall'elemento durante il drag
    swiperWrapper.addEventListener('pointerleave', () => {
      const images = slideImages();
      if (images.length > 0 && gsap) {
        gsap.to(images, {
          duration: 0.7,
          scale: 1
        });
      }
      removeCursorClass('drag-cursor');
    });

    // Event listener per mouseenter/mousemove - gestione cursore personalizzato
    swiperWrapper.addEventListener('mouseenter', () => {
      addCursorClass('scale-drag-x');
      
      // Verifica le classi per il tipo di cursore
      if (swiperContainer.classList.contains('light-cursor')) {
        addCursorClass('drag-cursor-white');
        updateBallCursor(0.2, '2px', 1, '#fff');
      } else if (swiperContainer.classList.contains('dark-cursor')) {
        addCursorClass('drag-cursor-black');
        updateBallCursor(0.2, '2px', 1, '#000');
      } else {
        // Cursore di default
        updateBallCursor(0.2, '2px', 1, getCursorBorderColor());
      }
    });

    swiperWrapper.addEventListener('mousemove', () => {
      addCursorClass('scale-drag-x');
      
      // Mantieni il tipo di cursore appropriato durante il movimento
      if (swiperContainer.classList.contains('light-cursor')) {
        addCursorClass('drag-cursor-white');
      } else if (swiperContainer.classList.contains('dark-cursor')) {
        addCursorClass('drag-cursor-black');
      }
    });

    // Event listener per mouseleave - ripristina cursore
    swiperWrapper.addEventListener('mouseleave', () => {
      updateBallCursor(0.2, '4px', 0.5, getCursorBorderColor());
      removeCursorClass('scale-drag-x');
      removeCursorClass('drag-cursor');
      removeCursorClass('drag-cursor-white');
      removeCursorClass('drag-cursor-black');
    });
  }

  // Event listener per mouseleave globale sul body - sicurezza aggiuntiva
  const handleBodyMouseLeave = () => {
    const images = slideImages();
    if (images.length > 0 && gsap) {
      gsap.to(images, {
        duration: 0.7,
        scale: 1
      });
    }
    removeCursorClass('scale-drag-x');
    removeCursorClass('drag-cursor');
    removeCursorClass('drag-cursor-white');
    removeCursorClass('drag-cursor-black');
  };

  document.body.addEventListener('mouseleave', handleBodyMouseLeave);

  // Cleanup function per rimuovere i listener quando necessario
  gallerySwiperInstance.cleanup = () => {
    document.body.removeEventListener('mouseleave', handleBodyMouseLeave);
  };

  return gallerySwiperInstance;
}

// Funzione per distruggere l'istanza e pulire i listener
export function destroyGallerySwiper() {
  if (gallerySwiperInstance) {
    if (gallerySwiperInstance.cleanup) {
      gallerySwiperInstance.cleanup();
    }
    gallerySwiperInstance.destroy(true, true);
    gallerySwiperInstance = null;
  }
} 